/**
 * StatusDiagram — the v2 System roll-up canvas (Flow L Frame 1; ADR-035 / S6).
 *
 * A bespoke SVG (storyboard OQ-2 lean — small fixed topology, air-gap-clean, no
 * graph library) rendering the composed nodes, the node-anchored edges and the
 * config-composed interface stacks. Presentational only: the panel resolves each
 * node's state/signal from the merged snapshot⊕stream and owns selection, the
 * selection bus and the aria-live announcer.
 *
 * # What v2 changed here
 *
 *   1. **No plane bands.** Nothing is drawn behind the nodes. The
 *      :prop:`TopologyNode.plane` attribute survives and is surfaced
 *      *textually* (Overall summary + each component's detail sub-heading), but
 *      this canvas draws no container for it.
 *   2. **Labels are centred, with the status glyph to their LEFT** — not above,
 *      not in a corner — and the glyph+label group is centred as a whole
 *      (REQ-STATUS-046; layout in :func:`layoutGlyphLabel`). No COTS name, no
 *      process name, no metric strip and no edge label renders on the canvas;
 *      those facts live in Selection Detail (REQ-STATUS-027).
 *   3. **Selection is ONE outline** — the element's own edge restroked in
 *      ``--ring`` — plus an increased-weight label (REQ-STATUS-059 amended
 *      2026-08-01). The offset halo that used to be drawn *in addition* is
 *      deleted. ⚠ ``--accent`` is shadcn's interaction-state *surface* slot
 *      (near-black on dark); ``--ring`` is the brand accent line colour. Painting
 *      selection in ``--accent`` outlines a node in ~its own background.
 *   4. **Interface stacks sit entirely outside their parent**, past a vertical
 *      rule that spans the *stack*, centred on the parent's centre line
 *      (REQ-STATUS-036 amended 2026-08-01). The parent is an ordinary fixed-size
 *      component, so a growing roster costs stack height and moves no edge.
 *
 * # Three states, three treatments (REQ-STATUS-061)
 *
 * ``selected`` (own edge, solid ``--ring``, bold label) · ``contains the
 * selection`` (dashed ring *outside* at offset 5, rhythm ``6 4``) · ``focused,
 * not selected`` (dashed ring outside at offset 11, finer rhythm ``2 3``). The
 * focus ring is **never suppressed** in favour of selection — it composes with
 * either of the other two at its own radius. Every affordance stroke carries
 * ``vector-effect="non-scaling-stroke"`` so it survives the fit scale
 * (REQ-STATUS-060).
 *
 * Cross-references:
 *   - Flow L §3 (roll-up view) / §9 (roving tabindex) / §10 (a11y); the mock
 *     header §1/§3/§6/§7 is the coordinate + treatment source of record.
 *   - REQ-UI-046 (state never by colour alone); REQ-UI-059 (keyboard/landmarks);
 *     REQ-STATUS-028 (planned marker); REQ-STATUS-038/-040 (icon state, cap);
 *     REQ-STATUS-047 (fill the panel against a variable extent);
 *     REQ-STATUS-063 (the sustained clock — see :func:`SustainedClock`).
 *   - ./status-topology (geometry); ./status-node-render (label + ring geometry).
 */
import { useCallback, useMemo, useRef, useState } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { rollupStateMeta, type RollupState } from "@/system-status/status-model";
import {
  CONTAINMENT_RING_DASH,
  CONTAINMENT_RING_OFFSET,
  CONTAINMENT_RING_WIDTH,
  FOCUS_RING_DASH,
  FOCUS_RING_OFFSET,
  FOCUS_RING_WIDTH,
  INTERFACE_FOCUS_RING_OFFSET,
  INTERFACE_GLYPH_RADIUS,
  LABEL_WEIGHT,
  NODE_GLYPH_RADIUS,
  NODE_LABEL_FONT_SIZE,
  SELECTED_LABEL_WEIGHT,
  SELECTION_STROKE_WIDTH,
  layoutGlyphLabel,
  nodeLabelLines,
} from "@/system-status/status-node-render";
import {
  composeExtent,
  edgePathD,
  type InterfaceIcon,
  type InterfaceStack,
  type RoutedEdge,
  type StackLevel,
  type TopologyNode,
} from "@/system-status/status-topology";

/** The resolved render inputs for one node (computed by the panel). */
export interface NodeRender {
  state: RollupState;
  signal: string | null;
}

/** The single inspected interface, as ``(parent node, interface name)``. */
export interface SelectedInterface {
  parentId: string;
  name: string;
}

/** Props for :func:`StatusDiagram`. */
export interface StatusDiagramProps {
  nodes: readonly TopologyNode[];
  /** Edges with their geometry already resolved from the composed nodes. */
  edges: readonly RoutedEdge[];
  /** Per-node resolved state + signal, keyed by node id (planned nodes omitted). */
  renders: ReadonlyMap<string, NodeRender>;
  /**
   * Composed interface stacks, at most one per node (:func:`composeInterfaceStack`).
   * Config-derived, so the drawing's extent follows them (REQ-STATUS-047).
   */
  stacks?: readonly InterfaceStack[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  /**
   * Activation of empty canvas — clears the selection (architect, 2026-08-01).
   *
   * Optional so a read-only embedding can simply not offer the affordance rather
   * than pass a no-op.
   */
  onBackgroundClick?: () => void;
  /**
   * The inspected stack icon, when the selection is at an icon level.
   *
   * Level-agnostic on purpose: an interface icon and a peer icon are both "one
   * icon at ``(parentId, name)``", each node hosts exactly one stack, and the
   * highlight is the same treatment either way. Splitting this into two props
   * would let a caller supply one and forget the other — which is exactly the
   * seam defect that left `selectedInterface` unpassed for a whole phase.
   */
  selectedInterface?: SelectedInterface | null;
  /**
   * Activation of a stack icon. ``level`` says which selection to originate —
   * ``status-peer`` (REQ-STATUS-081) or ``status-interface`` (REQ-STATUS-039) —
   * and is taken from the composed stack rather than re-derived from the node,
   * so the click and the icon it came from can never disagree about their level.
   */
  onSelectInterface?: (parentId: string, name: string, level: StackLevel) => void;
  /** Activation of the ``+K more`` slot — opens the node's full roster. */
  onOverflow?: (parentId: string) => void;
  /** Live-motion treatments (S6.6) — node ids currently flashing a transition. */
  transitioning?: ReadonlySet<string>;
  /** Node ids escalated by a sustained non-healthy state (red escalation). */
  sustained?: ReadonlyMap<string, number>;
  /** Node ids flagging a recent connection-churn burst. */
  bursts?: ReadonlySet<string>;
  /** Whether CSS/SMIL animations may play (= not reduced motion). */
  animate?: boolean;
}

// ---------------------------------------------------------------------------
// Tokens
// ---------------------------------------------------------------------------

// Edge / planned / separator strokes are non-text decoration — ``--decor-muted``
// keeps them deliberately dim (SC 1.4.11 exempt). Only ``TEXT_*`` below paints
// glyphs, so only those track the AA-bound text tokens.
const EDGE_COLOR = "hsl(var(--decor-muted))";
const EDGE_FUTURE_COLOR = "hsl(var(--decor-muted))";
/**
 * The Topology-A raw-bypass stroke. v2 tags **no** edge ``raw`` — the mock
 * carries the bypass in geometry (Ingress → Raw Data Store → Egress never
 * touches the Message Bus) rather than in a second colour — so this currently
 * renders like any data edge. It is deliberately **not** ``--accent``: that is
 * shadcn's interaction-state *surface* slot, and an edge painted in it is drawn
 * roughly in the panel's own background. Restoring a distinct raw tone needs a
 * real token decision (CLAUDE.md §6), not a reach for the nearest variable.
 */
const EDGE_RAW_COLOR = "hsl(var(--decor-muted))";
const BOX_STROKE = "hsl(var(--border))";
const TEXT_PRIMARY = "hsl(var(--text-primary))";
const TEXT_MUTED = "hsl(var(--text-muted))";
const PLANNED_STROKE = "hsl(var(--decor-muted))";
/**
 * The planned node's own body fill (mock ``--planned-fill``).
 *
 * ⚠ **Must never be null / ``transparent`` / ``none``.** The fill is set inline,
 * and an inline fill outranks any stylesheet rule — so a null here paints an
 * unbuilt capability with no body at all, rendering it identically to a live
 * component. That is the exact fail-open REQ-STATUS-028 and CLAUDE.md §8.1
 * forbid, and it is why stripping the on-canvas "PLANNED" text tag required a
 * replacement marker rather than a straight deletion.
 *
 * ``--meridian-surface-3`` is the production stand-in for the mock's dedicated
 * ``--planned-fill`` grey: neither the live-component surface nor the panel
 * behind it. Minting a dedicated token is a design-system decision (CLAUDE.md
 * §6) outside this change; the three non-colour channels (dashed stroke, hollow
 * dashed glyph, accessible name) carry the state meanwhile.
 */
const PLANNED_FILL = "hsl(var(--meridian-surface-3))";
/**
 * Input-modality tracking, so the focus ring behaves like CSS ``:focus-visible``.
 *
 * A pointer click both focuses and selects, and those land on different React
 * commits — so the focus ring painted for one frame and vanished as the selection
 * outline replaced it. That flash is what this removes (architect, 2026-08-01).
 * The ring is now drawn only when focus arrived from the **keyboard**, which is the
 * only case it exists for: a mouse user can see what they just clicked.
 *
 * Module-scope rather than React state on purpose — it is global input modality,
 * not per-node, and it must be updated in the capture phase *before* the focus
 * handler that reads it runs.
 */
let lastInputWasKeyboard = false;
if (typeof window !== "undefined") {
  window.addEventListener(
    "keydown",
    () => {
      lastInputWasKeyboard = true;
    },
    true,
  );
  window.addEventListener(
    "pointerdown",
    () => {
      lastInputWasKeyboard = false;
    },
    true,
  );
}

/** Whether a focus event should paint a visible focus ring (``:focus-visible`` semantics). */
function focusIsKeyboardDriven(): boolean {
  return lastInputWasKeyboard;
}

/**
 * Radius of the sustained clock's face, and its gap to the rollup mark
 * (REQ-STATUS-063 amended 2026-08-01).
 *
 * Sized just under :data:`NODE_GLYPH_RADIUS` so the pair reads as *rollup mark
 * plus a qualifier* rather than as two peer marks, and small enough that the
 * widened glyph+label group still fits the narrowest shape it can appear on:
 * Processed Data Store is the binding case at 136 units wide against a
 * ~127-unit group, leaving ~4.5 units of clearance inside the cylinder's
 * straight side. Widening either constant needs that case re-checked — the
 * "stays inside the DRAWN shape on every composed node" test is what checks it.
 */
const SUSTAINED_CLOCK_RADIUS = 5.5;
const SUSTAINED_CLOCK_GAP = 5;
/**
 * Horizontal room the clock costs the glyph+label group when it is present.
 * The group is re-centred around it (REQ-STATUS-046 keeps the whole group
 * centred), so the label shifts right by **half** this rather than the clock
 * hanging off the shape's left edge.
 */
const SUSTAINED_CLOCK_LEAD = 2 * SUSTAINED_CLOCK_RADIUS + SUSTAINED_CLOCK_GAP;

/**
 * Render a sustained duration compactly for the marker's tooltip / accessible name.
 * Coarse on purpose: an operator needs the order of magnitude ("4m", "2h"), and a
 * ticking seconds value inside an accessible name would make the node re-announce
 * itself on every frame.
 */
function formatSustainedDuration(ms: number): string {
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return "under a minute";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

/**
 * Extra pointer-target radius around the clock dial.
 *
 * The dial is a hairline stroke around a transparent fill, so hit-testing finds
 * only the outline; a few units of invisible padding turns it into the solid
 * target the operator already believes they are pointing at.
 */
const SUSTAINED_CLOCK_HIT_PAD = 3;

/**
 * Hover delay for the marker, shorter than the shared
 * :data:`TOOLTIP_DELAY_MS` used by the ``?`` affordances in Selection Detail.
 *
 * Those are *sought* — an operator moves to a question mark having decided to
 * ask. This marker is *encountered*: it appears unbidden on a node the operator
 * is already worried about, and the question it answers ("what is that?") is one
 * they are asking at the moment the pointer arrives. The standard delay reads as
 * the tooltip failing to come.
 */
const SUSTAINED_TOOLTIP_DELAY_MS = 200;

/**
 * What the marker says on hover.
 *
 * Two clauses on purpose: the *duration*, which the instantaneous rollup cannot
 * express, and what the clock **means** — a bare dial is a rebus until someone
 * tells you it is about persistence rather than severity. Operator register, no
 * identifiers and no internal vocabulary (architect, 2026-08-01).
 */
function sustainedTooltipText(ms: number): string {
  return `Not healthy for ${formatSustainedDuration(ms)}. The clock marks a problem that has persisted, rather than a brief flicker.`;
}

/** The brand accent line colour — selection, containment and focus (REQ-STATUS-059). */
const RING_COLOR = "hsl(var(--ring))";
/** The interface-stack separator rule (mock ``--sep``); non-text decoration. */
const SEPARATOR_STROKE = "hsl(var(--decor-muted))";

/**
 * The design system's monospace stack (``tailwind.config`` ``fontFamily.mono``),
 * restated here because SVG ``<text>`` takes a ``font-family`` attribute rather
 * than a utility class.
 *
 * ⚠ Keep in step with the Tailwind token — a divergence would silently change
 * the glyph advance that :data:`INTERFACE_LABEL_MAX_CHARS` is derived from.
 */
const INTERFACE_LABEL_FONT_FAMILY =
  "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

/** Interface-icon label size + inner offsets (mock §3 box anatomy). */
const INTERFACE_LABEL_FONT_SIZE = 9;
const INTERFACE_GLYPH_INSET = 11;
const INTERFACE_LABEL_INSET = 22;
/** Corner radii — component rectangles and interface boxes (mock). */
const NODE_CORNER_RADIUS = 9;
const INTERFACE_CORNER_RADIUS = 4;
/** Cylinder cap radius, and the label's downward nudge clear of it (mock ``nodeEl``). */
const CYLINDER_CAP_RY = 7;
const CYLINDER_LABEL_OFFSET = 3;

/** Stable empty default, so the ``stacks`` prop does not re-key memos each render. */
const NO_STACKS: readonly InterfaceStack[] = [];

// ---------------------------------------------------------------------------
// Status mark
// ---------------------------------------------------------------------------

/**
 * The distinct status mark — a shape unique per state, so the stoplight reads
 * without colour (REQ-UI-046): filled circle (healthy), up triangle (degraded),
 * diamond (unhealthy), hollow ring carrying a ``?`` (unknown), paused bars
 * (standby), hollow **dashed** ring (planned).
 *
 * The ``?`` is what separates *unknown* from *planned* at diagram scale — both
 * are hollow rings, and dash rhythm alone is a thin distinction on a 12-unit
 * mark. Two states that mean "no data" for opposite reasons ("we do not know"
 * versus "it was never built") must not be near-identical marks.
 *
 * The switch is on the model's ``glyph`` key rather than on the state, so the
 * shape vocabulary has exactly one owner: a state added to
 * :data:`ROLLUP_STATE_META` without a shape is a type error here rather than a
 * silent fall-through to the healthy circle — which is how ``standby`` first
 * shipped drawing itself as a grey *filled circle*, distinguishable from healthy
 * by hue alone and so failing REQ-UI-046 exactly where it matters most.
 *
 * ``data-mark`` carries the shape key into the DOM so the pairing is assertable
 * without pixel comparison.
 *
 * ⚠ Unhealthy is a **diamond** here where the mock draws an octagon. The mock
 * lists its own choice as open (§4 f / §5) and argues only against the reference
 * image's *red circle* — "a healthy glyph in another colour". A diamond is
 * already shape-distinct, is what ships today, and is what the legend, the
 * detail chips and the top-bar badge already reuse; overruling it is one branch.
 */
function StatusMark({
  state,
  cx,
  cy,
  planned,
  r = NODE_GLYPH_RADIUS,
}: {
  state: RollupState;
  cx: number;
  cy: number;
  planned: boolean;
  r?: number;
}): React.ReactElement {
  if (planned) {
    return (
      <circle
        data-mark="planned"
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke={PLANNED_STROKE}
        strokeWidth={1.5}
        strokeDasharray="2 2"
      />
    );
  }
  const { dotColor: color, glyph } = rollupStateMeta(state);
  switch (glyph) {
    case "warning":
      // Up triangle (degraded).
      return (
        <path
          data-mark="warning"
          d={`M${cx},${cy - r - 1} L${cx + r + 1},${cy + r} L${cx - r - 1},${cy + r} Z`}
          fill={color}
        />
      );
    case "cross":
      // Diamond (unhealthy).
      return (
        <path
          data-mark="cross"
          d={`M${cx},${cy - r - 0.5} L${cx + r + 0.5},${cy} L${cx},${cy + r + 0.5} L${cx - r - 0.5},${cy} Z`}
          fill={color}
        />
      );
    case "question":
      // Hollow ring + "?" (unknown — "we do not know").
      return (
        <g data-mark="question">
          <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={1.6} />
          <text
            x={cx}
            y={cy}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={r * 1.5}
            fontWeight={700}
            fill={color}
          >
            ?
          </text>
        </g>
      );
    case "pause":
      // Paused bars (standby — "held, and another replica is carrying it").
      return (
        <g data-mark="pause" fill={color}>
          <rect x={cx - r * 0.77} y={cy - r} width={r * 0.53} height={r * 2} rx={1} />
          <rect x={cx + r * 0.23} y={cy - r} width={r * 0.53} height={r * 2} rx={1} />
        </g>
      );
    case "stop":
      // A stop square (stopped — "off, and NOTHING is carrying it"). Shares
      // `pause`'s grey deliberately: neither is a fault, so neither should draw
      // the eye the way amber or red does — which means shape is carrying the
      // whole distinction here (REQ-UI-046, never by colour alone). One solid
      // square against two narrow bars is the widest shape separation available
      // without spending a sixth hue on the status palette.
      return (
        <rect
          data-mark="stop"
          x={cx - r * 0.8}
          y={cy - r * 0.8}
          width={r * 1.6}
          height={r * 1.6}
          rx={1}
          fill={color}
        />
      );
    case "check":
    default:
      return <circle data-mark="check" cx={cx} cy={cy} r={r} fill={color} />;
  }
}

/**
 * The **sustained** marker — a small clock drawn beside the node's rollup mark
 * (REQ-STATUS-063 amended 2026-08-01).
 *
 * # What it replaces, and why
 *
 * Sustained non-health was first drawn as a filled triangular wedge in the
 * node's bounding-box corner. The architect's verdict: *"it just looks like a
 * rendering bug rather than intentional information"* — fair, on three counts.
 * An unlabelled wedge in a corner has no read; it was emitted **before** the
 * body, so the body's own fill occluded everything but the sliver outside the
 * corner radius; and on a cylinder the bounding-box corner is not on the drawn
 * outline at all, so the sliver floated in empty space above-left of the body.
 *
 * A clock says the one thing the marker is for — *this has been going on for a
 * while* — and placing it on the rollup mark's own optical line makes it read as
 * a second piece of **status** rather than as a stray shape. Hand-drawn in SVG
 * like every other mark in this file; no icon-library dependency (CLAUDE.md
 * §4.3, §8.2).
 *
 * # Constraints it satisfies
 *
 *   - **Non-colour.** Painted in the node's own text colour, never a status
 *     token. This is the channel that stops the amber→red escalation being
 *     colour-only (REQ-UI-046), and painting it neutrally means it reads as a
 *     *mark* rather than a sixth state and needs no contrast measurement against
 *     the five status tints.
 *   - **Anchored to the drawn shape.** It sits on the label's optical line —
 *     which a cylinder already nudges clear of its cap (:data:`CYLINDER_LABEL_OFFSET`)
 *     — so it is inside the body on both shapes by construction rather than by
 *     a per-shape special case.
 *   - **No collisions.** The rollup mark is a full :data:`SUSTAINED_CLOCK_GAP`
 *     to its right; the burst badge owns the top-right, ~40 units above; and the
 *     selection / containment / focus affordances are all *outside* the body.
 *
 * The duration — the one thing an instantaneous rollup genuinely cannot express,
 * and what the retired panel banner carried — goes into the node's accessible
 * name and onto this marker's hover tooltip.
 *
 * # Why a Radix tooltip and not the SVG ``<title>`` it replaces
 *
 * A bare clock is a *rebus*: it says "duration" only to someone who already knows
 * that is what it means (architect, 2026-08-01). The marker therefore explains
 * itself on hover rather than only stating a number. ``<title>`` could not do
 * that job — the native tip is unstyled, fires after a ~1 s delay the operator
 * reads as unresponsiveness, and cannot be dismissed — and keeping both would
 * stack two tooltips on one hover, so it is removed rather than left as a
 * fallback.
 *
 * # Why the marker is ``aria-hidden`` and carries no tab stop
 *
 * The host node is already ``role="button"`` with a roving tabindex, and nesting
 * a focusable control inside a button is invalid ARIA — it would also put a
 * second tab stop inside every escalated node, changing the traversal order the
 * keyboard contract depends on (REQ-UI-059). Nothing is lost by hiding it: the
 * node's own accessible name already ends *"sustained for 4m"*, so a screen
 * reader announces the duration on reaching the node, without this marker
 * repeating it. The tooltip is a **pointer** affordance layered over an AT path
 * that already exists — not the only way to the fact.
 */
function SustainedClock({
  nodeId,
  cx,
  cy,
  ms,
}: {
  nodeId: string;
  cx: number;
  cy: number;
  /** How long the node has been continuously non-healthy. */
  ms: number;
}): React.ReactElement {
  const r = SUSTAINED_CLOCK_RADIUS;
  return (
    <Tooltip delayDuration={SUSTAINED_TOOLTIP_DELAY_MS}>
      <TooltipTrigger asChild>
        <g
          data-testid={`node-sustained-${nodeId}`}
          data-sustained-ms={ms}
          aria-hidden="true"
          style={{ cursor: "help" }}
        >
          {/* A transparent hit area: the dial is a 1.4-unit stroke around empty
              fill, so without this the pointer only "finds" the marker on the
              outline itself and the tooltip flickers as the cursor crosses it. */}
          <circle
            data-sustained-hit="true"
            cx={cx}
            cy={cy}
            r={r + SUSTAINED_CLOCK_HIT_PAD}
            fill="transparent"
            stroke="none"
          />
          <circle
            data-sustained-face="true"
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={TEXT_PRIMARY}
            strokeWidth={1.4}
          />
          {/* Two hands meeting at the centre, pointing 12 and 3. Drawn as ONE
              polyline so the join at the centre is a join rather than two strokes
              crossing — at this size, overlapping caps read as a blob. */}
          <path
            data-sustained-hands="true"
            d={`M ${cx} ${cy - r * 0.62} L ${cx} ${cy} L ${cx + r * 0.5} ${cy}`}
            fill="none"
            stroke={TEXT_PRIMARY}
            strokeWidth={1.4}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
      </TooltipTrigger>
      <TooltipContent data-testid={`node-sustained-${nodeId}-tip`}>
        {sustainedTooltipText(ms)}
      </TooltipContent>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// Rings
// ---------------------------------------------------------------------------

/**
 * A containment / focus ring, offset outward from a node's bounds. A rect for
 * box nodes; a matching cylinder path + cap ellipse for ``cyl`` nodes, so the
 * ring is a **shape** cue that reads over any status tint.
 *
 * Never used for selection (REQ-STATUS-059 amended 2026-08-01): a selected node
 * is marked by its own outline plus a bold label. The two callers differ in both
 * radius and dash rhythm, so containment and focus never read as one treatment.
 */
function OutlineRing({
  node,
  offset,
  dash,
  width,
  kind,
}: {
  node: TopologyNode;
  offset: number;
  dash: string;
  width: number;
  kind: "containment" | "focus";
}): React.ReactElement {
  const common = {
    "data-ring": kind,
    fill: "none",
    stroke: RING_COLOR,
    strokeWidth: width,
    strokeDasharray: dash,
    // REQ-STATUS-060 — a constant device width irrespective of the fit scale.
    vectorEffect: "non-scaling-stroke" as const,
  };
  const x = node.x - offset;
  const y = node.y - offset;
  const w = node.w + 2 * offset;
  const h = node.h + 2 * offset;
  if (node.shape !== "cyl") {
    return <rect {...common} x={x} y={y} width={w} height={h} rx={NODE_CORNER_RADIUS + offset} />;
  }
  const ry = CYLINDER_CAP_RY + offset / 2;
  return (
    <>
      <path
        {...common}
        d={`M ${x},${y + ry} L ${x},${y + h - ry} A ${w / 2},${ry} 0 0 0 ${x + w},${y + h - ry} L ${x + w},${y + ry}`}
      />
      <ellipse {...common} cx={x + w / 2} cy={y + ry} rx={w / 2} ry={ry} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Node
// ---------------------------------------------------------------------------

/** The glyph + centred multi-line label group (REQ-STATUS-046). */
function GlyphLabel({
  lines,
  state,
  planned,
  cx,
  cy,
  radius,
  fontSize,
  weight,
  labelAttr,
  labelFill = TEXT_PRIMARY,
  withGlyph = true,
}: {
  lines: readonly string[];
  state: RollupState;
  planned: boolean;
  cx: number;
  cy: number;
  radius: number;
  fontSize: number;
  weight: number;
  labelAttr: string;
  labelFill?: string;
  withGlyph?: boolean;
}): React.ReactElement {
  const layout = layoutGlyphLabel(lines, cx, cy, radius, fontSize);
  return (
    <>
      {withGlyph && (
        <StatusMark state={state} cx={layout.glyphCx} cy={cy} planned={planned} r={radius} />
      )}
      {lines.map((line, i) => (
        <text
          key={`${line}-${i}`}
          {...{ [labelAttr]: "true" }}
          x={layout.labelX}
          y={layout.lineYs[i]}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={fontSize}
          fontWeight={weight}
          fill={labelFill}
        >
          {line}
        </text>
      ))}
    </>
  );
}

/** The two-element cylinder body for the object/data stores (MinIO, Postgres). */
function CylinderBody({
  node,
  fill,
  stroke,
  strokeWidth,
  selected,
}: {
  node: TopologyNode;
  fill: string;
  stroke: string;
  strokeWidth: number;
  selected: boolean;
}): React.ReactElement {
  const ry = CYLINDER_CAP_RY;
  const cx = node.x + node.w / 2;
  const common = {
    "data-node-body": "true",
    fill,
    stroke,
    strokeWidth,
    ...(selected ? { vectorEffect: "non-scaling-stroke" as const } : {}),
  };
  return (
    <>
      <path
        {...common}
        d={`M ${node.x},${node.y + ry} L ${node.x},${node.y + node.h - ry} A ${node.w / 2},${ry} 0 0 0 ${node.x + node.w},${node.y + node.h - ry} L ${node.x + node.w},${node.y + ry}`}
      />
      <ellipse {...common} cx={cx} cy={node.y + ry} rx={node.w / 2} ry={ry} />
    </>
  );
}

/** Render one node group — body, rings, glyph + centred label, live motion. */
function NodeShape({
  node,
  render,
  selected,
  containsSelection,
  focused,
  tabIndex,
  nodeRef,
  onSelect,
  onKeyDown,
  onFocus,
  onBlur,
  transitioning = false,
  sustainedMs,
  burst = false,
  animate = true,
}: {
  node: TopologyNode;
  render: NodeRender | undefined;
  selected: boolean;
  containsSelection: boolean;
  focused: boolean;
  tabIndex: number;
  nodeRef: (el: SVGGElement | null) => void;
  onSelect: () => void;
  onKeyDown: (e: React.KeyboardEvent<SVGGElement>) => void;
  onFocus: () => void;
  onBlur: () => void;
  transitioning?: boolean;
  /** How long this node has been non-healthy (ms), or undefined when not sustained. */
  sustainedMs?: number;
  burst?: boolean;
  animate?: boolean;
}): React.ReactElement {
  const state: RollupState = node.planned ? "unknown" : (render?.state ?? "unknown");
  const meta = rollupStateMeta(state);
  const bodyFill = node.planned ? PLANNED_FILL : meta.tintFill;
  const stroke = selected ? RING_COLOR : node.planned ? PLANNED_STROKE : BOX_STROKE;
  const strokeWidth = selected ? SELECTION_STROKE_WIDTH : 1;
  // A cylinder's label clears its top cap; a box centres on its own middle.
  const labelCy = node.shape === "cyl" ? node.cy + CYLINDER_LABEL_OFFSET : node.cy;
  const lines = nodeLabelLines(node);
  // The sustained clock joins the glyph+label group as a leading item, and the
  // WHOLE group stays centred on the shape (REQ-STATUS-046) — so the mark+label
  // shift right by half the clock's lead rather than the clock hanging off the
  // left edge. `layoutGlyphLabel` stays the single owner of the group's
  // arithmetic: this offsets its input and reads its output, never restates it.
  const clockLead = sustainedMs === undefined ? 0 : SUSTAINED_CLOCK_LEAD;
  const groupCx = node.x + node.w / 2 + clockLead / 2;
  const clockCx =
    layoutGlyphLabel(lines, groupCx, labelCy, NODE_GLYPH_RADIUS, NODE_LABEL_FONT_SIZE).glyphCx -
    NODE_GLYPH_RADIUS -
    SUSTAINED_CLOCK_GAP -
    SUSTAINED_CLOCK_RADIUS;
  const ariaLabel = node.planned
    ? `${node.name} — planned capability, no status collected`
    : sustainedMs !== undefined
      ? `${node.name}, ${meta.label}, sustained for ${formatSustainedDuration(sustainedMs)}`
      : `${node.name}, ${meta.label}`;

  return (
    <g
      ref={nodeRef}
      role="button"
      tabIndex={tabIndex}
      data-testid={`status-node-${node.id}`}
      data-state={node.planned ? "planned" : state}
      data-sustained={sustainedMs !== undefined ? "true" : undefined}
      data-burst={burst ? "true" : undefined}
      aria-label={ariaLabel}
      aria-pressed={selected}
      onClick={onSelect}
      onKeyDown={onKeyDown}
      onFocus={onFocus}
      onBlur={onBlur}
      style={{ cursor: "pointer", outline: "none" }}
    >
      {/* Rings first, so they pass BEHIND the body they surround rather than
          across it. Focus is drawn whether or not the node is selected or
          contains the selection — three treatments at three radii, never one
          suppressing another (REQ-STATUS-061). */}
      {containsSelection && (
        <OutlineRing
          node={node}
          offset={CONTAINMENT_RING_OFFSET}
          dash={CONTAINMENT_RING_DASH}
          width={CONTAINMENT_RING_WIDTH}
          kind="containment"
        />
      )}
      {/* Focus is drawn only when it is NOT already carried by the selection outline
          (architect, 2026-08-01). A click both selects and focuses, so drawing both
          put a dashed ring outside a solid one on the same element — read as two
          rings, which is exactly what item 3 removed. REQ-STATUS-061's guarantee is
          that focus is never suppressed *in favour of* selection where the two
          DIFFER — a keyboard user tabbing to an unselected node must still see it —
          and that is preserved: this only collapses the redundant case where focus
          and selection are the same element. */}
      {focused && !selected && focusIsKeyboardDriven() && (
        <OutlineRing
          node={node}
          offset={FOCUS_RING_OFFSET}
          dash={FOCUS_RING_DASH}
          width={FOCUS_RING_WIDTH}
          kind="focus"
        />
      )}
      {node.shape === "cyl" ? (
        <CylinderBody
          node={node}
          fill={bodyFill}
          stroke={stroke}
          strokeWidth={strokeWidth}
          selected={selected}
        />
      ) : (
        <rect
          data-node-body="true"
          x={node.x}
          y={node.y}
          width={node.w}
          height={node.h}
          rx={NODE_CORNER_RADIUS}
          fill={bodyFill}
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeDasharray={node.planned ? "6 4" : undefined}
          // The node's own outline IS the selection ring since v2, so it carries
          // the non-scaling stroke that used to live on the deleted halo.
          vectorEffect={selected ? "non-scaling-stroke" : undefined}
        />
      )}
      <GlyphLabel
        lines={lines}
        state={state}
        planned={node.planned}
        cx={groupCx}
        cy={labelCy}
        radius={NODE_GLYPH_RADIUS}
        fontSize={NODE_LABEL_FONT_SIZE}
        weight={selected ? SELECTED_LABEL_WEIGHT : LABEL_WEIGHT}
        labelAttr="data-node-label"
      />
      {/* Emitted AFTER the body — the wedge this replaces was drawn before it and
          was occluded by the body's own fill everywhere but the sliver outside the
          corner radius, which is half of why it read as an artefact. */}
      {sustainedMs !== undefined && (
        <SustainedClock nodeId={node.id} cx={clockCx} cy={labelCy} ms={sustainedMs} />
      )}
      {/* Stoplight-transition pulse (§8/§9) — only present when motion is allowed
          (the controller leaves the set empty under reduced motion). */}
      {transitioning && (
        <circle
          cx={node.x + node.w / 2}
          cy={labelCy}
          r={NODE_GLYPH_RADIUS}
          fill="none"
          stroke={meta.dotColor}
          strokeWidth={2}
          data-testid={`node-transition-${node.id}`}
        >
          <animate attributeName="r" values="6;15" dur="0.9s" repeatCount="1" />
          <animate attributeName="opacity" values="0.9;0" dur="0.9s" repeatCount="1" />
        </circle>
      )}
      {/* The inline summary signal is NOT drawn on the node (architect, 2026-08-01).
          It was quantitative detail — "cpu 22%", "consumer_lag=40038 (warn 2000 /
          crit 10000)" — rendered under the centred label, and at realistic values it
          overflowed the node on both sides (worst on the narrow Raw Data Store) and
          cluttered the canvas it was meant to annotate. The canvas carries STATE
          (mark shape + tint + accessible name); the numbers behind that state belong
          in Selection Detail, where there is room to read them and a tooltip for the
          full text. `render.signal` is still resolved by `resolveNodeRender` and still
          feeds Selection Detail; it is simply no longer painted here. The node's
          accessible name carries `name, state` — the state, not the numbers — so the
          canvas reads the same to a screen reader as it does visually, and the detail
          is equally reachable for everyone by selecting the node. */}
      {/* Connection-churn burst badge (§11) — a state indicator (shown regardless
          of motion); the opacity pulse is gated on ``animate``. */}
      {burst && (
        <g data-testid={`node-burst-${node.id}`}>
          <rect
            x={node.x + node.w - 48}
            y={node.y - 9}
            width={44}
            height={16}
            rx={8}
            fill="hsl(var(--accent))"
          >
            {animate && (
              <animate
                attributeName="opacity"
                values="1;0.55;1"
                dur="1.2s"
                repeatCount="indefinite"
              />
            )}
          </rect>
          <text
            x={node.x + node.w - 26}
            y={node.y + 2}
            fontSize={9}
            fontWeight={600}
            textAnchor="middle"
            fill={TEXT_PRIMARY}
          >
            churn
          </text>
        </g>
      )}
      {node.planned && <title>{ariaLabel}</title>}
    </g>
  );
}

// ---------------------------------------------------------------------------
// Interface stack
// ---------------------------------------------------------------------------

/** One stack icon — an interface or a peer; status glyph on the left, truncated label beside it. */
function InterfaceBox({
  icon,
  level,
  selected,
  focused,
  iconRef,
  onSelect,
  onKeyDown,
  onFocus,
  onBlur,
}: {
  icon: InterfaceIcon;
  /** Which level this icon is — drives its ``data-testid`` prefix only. */
  level: StackLevel;
  selected: boolean;
  focused: boolean;
  iconRef: (el: SVGGElement | null) => void;
  onSelect: () => void;
  onKeyDown: (e: React.KeyboardEvent<SVGGElement>) => void;
  onFocus: () => void;
  onBlur: () => void;
}): React.ReactElement {
  const meta = rollupStateMeta(icon.state);
  const midY = icon.y + icon.height / 2;
  return (
    <g
      ref={iconRef}
      role="button"
      tabIndex={-1}
      // `status-peer-…` / `status-interface-…`, deliberately NOT a shared prefix:
      // the two levels are separately selectable, and a selector written against a
      // common prefix would match whichever the diagram happened to draw.
      data-testid={`status-${level}-${icon.name}`}
      data-state={icon.state}
      data-level={level}
      // The FULL, untruncated name on the accessible name, the testid and the
      // tooltip — no selector and no API call ever depends on the display budget.
      aria-label={`${icon.name}, ${meta.label}`}
      onClick={(e) => {
        // The parent node is a sibling group, not an ancestor, but the click
        // still bubbles to the SVG root; stopping it keeps an icon activation
        // from also reading as a node activation (REQ-UI-051 — one focus).
        e.stopPropagation();
        onSelect();
      }}
      onKeyDown={onKeyDown}
      onFocus={onFocus}
      onBlur={onBlur}
      style={{ cursor: "pointer", outline: "none" }}
    >
      {/* Same rules as the node: no ring on an already-selected icon, and only for
          keyboard-driven focus — a pointer click would otherwise flash one. */}
      {focused && !selected && focusIsKeyboardDriven() && (
        <rect
          data-ring="focus"
          x={icon.x - INTERFACE_FOCUS_RING_OFFSET}
          y={icon.y - INTERFACE_FOCUS_RING_OFFSET}
          width={icon.width + 2 * INTERFACE_FOCUS_RING_OFFSET}
          height={icon.height + 2 * INTERFACE_FOCUS_RING_OFFSET}
          rx={INTERFACE_CORNER_RADIUS + INTERFACE_FOCUS_RING_OFFSET}
          fill="none"
          stroke={RING_COLOR}
          strokeWidth={FOCUS_RING_WIDTH}
          strokeDasharray={FOCUS_RING_DASH}
          vectorEffect="non-scaling-stroke"
        />
      )}
      <rect
        data-interface-body="true"
        x={icon.x}
        y={icon.y}
        width={icon.width}
        height={icon.height}
        rx={INTERFACE_CORNER_RADIUS}
        fill={meta.tintFill}
        stroke={selected ? RING_COLOR : BOX_STROKE}
        strokeWidth={selected ? SELECTION_STROKE_WIDTH : 1}
        vectorEffect={selected ? "non-scaling-stroke" : undefined}
      />
      <StatusMark
        state={icon.state}
        cx={icon.x + INTERFACE_GLYPH_INSET}
        cy={midY}
        planned={false}
        r={INTERFACE_GLYPH_RADIUS}
      />
      {/* MONOSPACE is load-bearing, not styling (REQ-STATUS-037). It is the only
          reason INTERFACE_LABEL_MAX_CHARS is a *width* rather than a guess: in a
          proportional face the widest 16 glyphs and the narrowest differ by more
          than the box's slack, and SVG text does not clip, so an over-budget
          label would spill out of its box rather than being cut off. Change this
          family and the budget in `status-topology.ts` must be re-derived. */}
      <text
        data-interface-label="true"
        x={icon.x + INTERFACE_LABEL_INSET}
        y={midY}
        dominantBaseline="central"
        fontFamily={INTERFACE_LABEL_FONT_FAMILY}
        fontSize={INTERFACE_LABEL_FONT_SIZE}
        fontWeight={selected ? SELECTED_LABEL_WEIGHT : 400}
        fill={TEXT_PRIMARY}
      >
        {icon.label}
      </text>
      <title>{`${icon.name} — ${meta.label}`}</title>
    </g>
  );
}

/**
 * One node's interface stack: the icons, the ``+K more`` slot, and the vertical
 * rule that separates the whole stack from its parent.
 *
 * The rule **spans the stack**, so its length carries information — a short tick
 * at one interface, a full-height rule at the cap. It is drawn only when the
 * stack has height: a structural Peer Manager node with nothing configured
 * (REQ-STATUS-041) composes zero slots, and a zero-length (or, before the
 * composer guarded it, inside-out) rule is a rendering artefact asserting a
 * separation that is not there.
 *
 * ⚠ **This renderer does not decide which interfaces get an icon.** Whether a
 * **CDS** peer interface is icon-bearing is an open architect ruling
 * (REQ-STATUS-035 Notes; feature-spec §11), and it is owned by the single named
 * left join in ``composeInterfaceStack`` (CDS is not special — issue #754). This
 * component draws exactly the icons :func:`composeInterfaceStack` handed it — so
 * applying the ruling stays a one-line change there and never becomes a hunt
 * through the drawing code.
 */
function InterfaceStackGroup({
  stack,
  parentName,
  selectedName,
  focusedKey,
  registerRef,
  onSelectInterface,
  onOverflow,
  onKeyDown,
  onFocusSlot,
  onBlurSlot,
}: {
  stack: InterfaceStack;
  parentName: string;
  selectedName: string | null;
  focusedKey: string | null;
  registerRef: (key: string, el: SVGGElement | null) => void;
  onSelectInterface: (parentId: string, name: string, level: StackLevel) => void;
  onOverflow: (parentId: string) => void;
  onKeyDown: (parentId: string, index: number, slots: number) => (e: React.KeyboardEvent) => void;
  onFocusSlot: (key: string) => void;
  onBlurSlot: (key: string) => void;
}): React.ReactElement {
  const slotKey = (index: number): string => `${stack.parentId}:${index}`;
  const totalConfigured = stack.icons.length + (stack.overflow?.count ?? 0);
  // The group names the LEVEL it enumerates, not a fixed noun. It said
  // "interfaces" on every stack until the ADR-039 Phase I §5.11 live pass caught
  // it announcing "Peer Manager interfaces" over a stack of peers — a
  // screen-reader user told the wrong level for icons that were already labelled
  // correctly one element in. Nothing visual was wrong, so nothing but the
  // accessible name could have surfaced it.
  const groupNoun = stack.level === "peer" ? "peers" : "interfaces";
  return (
    <g role="group" aria-label={`${parentName} ${groupNoun}`}>
      {/* Guarded on the rule's OWN length, not on a proxy for it: a zero-length
          line and an inside-out one (bottom above top) are both artefacts that
          assert a separation which is not there, and the guard should be on the
          quantity actually drawn. */}
      {stack.separatorBottom > stack.separatorTop && (
        <line
          data-testid={`interface-separator-${stack.parentId}`}
          x1={stack.separatorX}
          y1={stack.separatorTop}
          x2={stack.separatorX}
          y2={stack.separatorBottom}
          stroke={SEPARATOR_STROKE}
          strokeWidth={1.4}
        />
      )}
      {stack.icons.map((icon, i) => (
        <InterfaceBox
          key={icon.name}
          icon={icon}
          level={stack.level}
          selected={selectedName === icon.name}
          focused={focusedKey === slotKey(i)}
          iconRef={(el) => registerRef(slotKey(i), el)}
          onSelect={() => onSelectInterface(stack.parentId, icon.name, stack.level)}
          onKeyDown={onKeyDown(stack.parentId, i, stack.slots)}
          onFocus={() => onFocusSlot(slotKey(i))}
          onBlur={() => onBlurSlot(slotKey(i))}
        />
      ))}
      {stack.overflow !== null && (
        <g
          ref={(el) => registerRef(slotKey(stack.icons.length), el)}
          role="button"
          tabIndex={-1}
          data-testid={`status-${stack.level}-overflow-${stack.parentId}`}
          aria-label={`Show all ${totalConfigured} ${stack.level === "peer" ? "peers" : "interfaces"}`}
          onClick={(e) => {
            e.stopPropagation();
            onOverflow(stack.parentId);
          }}
          onKeyDown={onKeyDown(stack.parentId, stack.icons.length, stack.slots)}
          onFocus={() => onFocusSlot(slotKey(stack.icons.length))}
          onBlur={() => onBlurSlot(slotKey(stack.icons.length))}
          style={{ cursor: "pointer", outline: "none" }}
        >
          {focusedKey === slotKey(stack.icons.length) && (
            <rect
              data-ring="focus"
              x={stack.overflow.x - INTERFACE_FOCUS_RING_OFFSET}
              y={stack.overflow.y - INTERFACE_FOCUS_RING_OFFSET}
              width={stack.overflow.width + 2 * INTERFACE_FOCUS_RING_OFFSET}
              height={stack.overflow.height + 2 * INTERFACE_FOCUS_RING_OFFSET}
              rx={INTERFACE_CORNER_RADIUS + INTERFACE_FOCUS_RING_OFFSET}
              fill="none"
              stroke={RING_COLOR}
              strokeWidth={FOCUS_RING_WIDTH}
              strokeDasharray={FOCUS_RING_DASH}
              vectorEffect="non-scaling-stroke"
            />
          )}
          {/* Not an interface, so it takes neither the status fill nor a glyph —
              it is an affordance, and dressing it as an interface would invent a
              tenth flow that does not exist. */}
          <rect
            x={stack.overflow.x}
            y={stack.overflow.y}
            width={stack.overflow.width}
            height={stack.overflow.height}
            rx={INTERFACE_CORNER_RADIUS}
            fill="none"
            stroke={BOX_STROKE}
            strokeDasharray="3 3"
          />
          <text
            x={stack.overflow.x + INTERFACE_LABEL_INSET}
            y={stack.overflow.y + stack.overflow.height / 2}
            dominantBaseline="central"
            fontSize={INTERFACE_LABEL_FONT_SIZE}
            fontStyle="italic"
            fill={TEXT_MUTED}
          >
            {stack.overflow.label}
          </text>
        </g>
      )}
    </g>
  );
}

// ---------------------------------------------------------------------------
// Edges
// ---------------------------------------------------------------------------

/**
 * Render one **routed** edge with the appropriate arrowhead + stroke class.
 *
 * The geometry arrives already resolved from the two endpoint nodes
 * (:func:`routeEdge`) — this component picks the element, never the coordinates.
 * A two-point route draws as a ``<line>`` and anything longer as an orthogonal
 * ``<path>``.
 *
 * **No edge carries a label** (REQ-STATUS-046): the relationships they used to
 * name are in each node's Selection Detail "Interacts with" roster instead.
 */
function Edge({ edge }: { edge: RoutedEdge }): React.ReactElement {
  const isFuture = edge.kind === "future";
  const isRaw = edge.kind === "raw";
  const stroke = isFuture ? EDGE_FUTURE_COLOR : isRaw ? EDGE_RAW_COLOR : EDGE_COLOR;
  const common = {
    stroke,
    strokeWidth: 1.6,
    strokeDasharray: isFuture ? "5 4" : undefined,
    fill: "none",
    markerEnd: isFuture ? "url(#ss-arrow-future)" : "url(#ss-arrow)",
    markerStart: edge.bidirectional ? "url(#ss-arrow-start)" : undefined,
  };
  const start = edge.points[0];
  const finish = edge.points[edge.points.length - 1];
  return edge.points.length === 2 ? (
    <line x1={start.x} y1={start.y} x2={finish.x} y2={finish.y} {...common} />
  ) : (
    <path d={edgePathD(edge.points)} {...common} />
  );
}

// ---------------------------------------------------------------------------
// Diagram
// ---------------------------------------------------------------------------

/**
 * The roll-up diagram.
 *
 * **Keyboard model** (REQ-UI-059 / REQ-STATUS-039). Component nodes are the tab
 * stops (roving tabindex); interface icons are children of their parent node and
 * are reached from it, so every icon is keyboard-reachable without adding N tab
 * stops to the page. ←/→/↑ move between nodes; ↓ descends into a node's stack
 * when it has one, otherwise moves to the next node; inside a stack ↑/↓ move
 * between icons (clamped, so ↓ at the bottom does not silently wrap out of the
 * stack) and ←/→/Esc return to the parent; Enter / Space activates.
 *
 * **Panel fill** (REQ-STATUS-047). The SVG is sized ``w-full h-auto``, not
 * ``h-full``: the drawing scales to the panel's width and its own height follows
 * the composed extent, so there is no letterboxed margin inside a fixed box, and
 * the host container (already ``overflow-auto``) scrolls if the composition is
 * taller than the panel. The extent itself is recomputed from the nodes **and
 * the composed stacks**, so a roster near the display cap cannot be clipped.
 */
export function StatusDiagram({
  nodes,
  edges,
  renders,
  stacks = NO_STACKS,
  selectedId,
  onSelect,
  selectedInterface = null,
  onBackgroundClick,
  onSelectInterface,
  onOverflow,
  transitioning,
  sustained,
  bursts,
  animate = true,
}: StatusDiagramProps): React.ReactElement {
  const [focusIndex, setFocusIndex] = useState(0);
  // Which element currently HOLDS focus — distinct from `focusIndex`, which is
  // the roving tab stop and must survive blur. REQ-STATUS-061 requires the focus
  // affordance to be its own state, never folded into selection.
  const [focusedKey, setFocusedKey] = useState<string | null>(null);
  const nodeRefs = useRef<(SVGGElement | null)[]>([]);
  const interfaceRefs = useRef<Map<string, SVGGElement | null>>(new Map());

  const extent = useMemo(() => composeExtent(nodes, stacks), [nodes, stacks]);
  const stacksByParent = useMemo(
    () => new Map(stacks.map((stack) => [stack.parentId, stack])),
    [stacks],
  );
  const indexById = useMemo(() => new Map(nodes.map((n, i) => [n.id, i])), [nodes]);

  const registerInterfaceRef = useCallback((key: string, el: SVGGElement | null): void => {
    interfaceRefs.current.set(key, el);
  }, []);

  const focusNodeAt = useCallback(
    (next: number): void => {
      const count = nodes.length;
      if (count === 0) return;
      const wrapped = ((next % count) + count) % count;
      setFocusIndex(wrapped);
      nodeRefs.current[wrapped]?.focus();
    },
    [nodes.length],
  );

  const focusSlot = useCallback((parentId: string, index: number): void => {
    interfaceRefs.current.get(`${parentId}:${index}`)?.focus();
  }, []);

  const handleNodeKeyDown =
    (index: number) =>
    (e: React.KeyboardEvent<SVGGElement>): void => {
      const stack = stacksByParent.get(nodes[index].id);
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          if (stack !== undefined && stack.slots > 0) focusSlot(nodes[index].id, 0);
          else focusNodeAt(index + 1);
          break;
        case "ArrowRight":
          e.preventDefault();
          focusNodeAt(index + 1);
          break;
        case "ArrowLeft":
        case "ArrowUp":
          e.preventDefault();
          focusNodeAt(index - 1);
          break;
        case "Enter":
        case " ":
          e.preventDefault();
          onSelect(nodes[index].id);
          break;
        default:
          break;
      }
    };

  const handleSlotKeyDown =
    (parentId: string, index: number, slots: number) =>
    (e: React.KeyboardEvent): void => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          // Clamped, not wrapped: ↓ at the bottom of a stack must not teleport
          // to its top, which reads as "nothing happened" and loses the operator.
          focusSlot(parentId, Math.min(index + 1, slots - 1));
          break;
        case "ArrowUp":
          e.preventDefault();
          focusSlot(parentId, Math.max(index - 1, 0));
          break;
        case "ArrowLeft":
        case "ArrowRight":
        case "Escape": {
          e.preventDefault();
          const parentIndex = indexById.get(parentId);
          if (parentIndex !== undefined) focusNodeAt(parentIndex);
          break;
        }
        case "Enter":
        case " ": {
          e.preventDefault();
          const stack = stacksByParent.get(parentId);
          const icon = stack?.icons[index];
          if (icon !== undefined && stack !== undefined)
            onSelectInterface?.(parentId, icon.name, stack.level);
          else if (stack?.overflow != null) onOverflow?.(parentId);
          break;
        }
        default:
          break;
      }
    };

  return (
    <svg
      viewBox={`0 0 ${extent.viewBox.width} ${extent.viewBox.height}`}
      role="group"
      aria-label="System status data-flow diagram"
      data-testid="status-diagram"
      // ``h-auto`` (not ``h-full``): the element takes the drawing's own aspect
      // ratio at the panel's width, so nothing is letterboxed inside a fixed box
      // (REQ-STATUS-047). The host container scrolls.
      className="block h-auto w-full"
      preserveAspectRatio="xMidYMid meet"
      // Clicking empty canvas clears the selection (architect, 2026-08-01) — the
      // same escape a second click on the selected node gives, reachable without
      // having to find that node again.
      //
      // The guard is what makes it safe: nodes and icons already
      // `stopPropagation()` on their own clicks, but a future child that forgets
      // to would silently deselect itself the moment it was clicked. Requiring
      // the event to have STARTED on the svg itself means only genuine
      // background hits qualify, whatever descendants do.
      onClick={(event) => {
        if (event.target === event.currentTarget) onBackgroundClick?.();
      }}
    >
      <defs>
        <marker
          id="ss-arrow"
          markerWidth={8}
          markerHeight={8}
          refX={6}
          refY={3}
          orient="auto"
          markerUnits="strokeWidth"
        >
          <path d="M0,0 L6,3 L0,6 Z" fill={EDGE_COLOR} />
        </marker>
        <marker
          id="ss-arrow-future"
          markerWidth={8}
          markerHeight={8}
          refX={6}
          refY={3}
          orient="auto"
          markerUnits="strokeWidth"
        >
          <path d="M0,0 L6,3 L0,6 Z" fill={EDGE_FUTURE_COLOR} />
        </marker>
        <marker
          id="ss-arrow-start"
          markerWidth={8}
          markerHeight={8}
          refX={2}
          refY={3}
          orient="auto"
          markerUnits="strokeWidth"
        >
          <path d="M6,0 L0,3 L6,6 Z" fill={EDGE_COLOR} />
        </marker>
      </defs>

      {/* Draw order: edges, then stacks, then node bodies. Nothing overlaps in
          the v2 layout, so this occludes nothing — it keeps the picture stable
          if a future composition ever does. No plane bands are drawn. */}
      {edges.map((edge, i) => (
        <Edge key={`edge-${i}`} edge={edge} />
      ))}

      {nodes.map((node) => {
        const stack = stacksByParent.get(node.id);
        // A structural node with nothing configured (REQ-STATUS-041) draws no
        // group at all — an empty labelled ARIA group announces a container a
        // screen-reader user then finds nothing inside.
        if (stack === undefined || stack.slots === 0) return null;
        return (
          <InterfaceStackGroup
            key={`stack-${node.id}`}
            stack={stack}
            parentName={node.name}
            selectedName={selectedInterface?.parentId === node.id ? selectedInterface.name : null}
            focusedKey={focusedKey}
            registerRef={registerInterfaceRef}
            onSelectInterface={(parentId, name, level) =>
              onSelectInterface?.(parentId, name, level)
            }
            onOverflow={(parentId) => onOverflow?.(parentId)}
            onKeyDown={handleSlotKeyDown}
            onFocusSlot={setFocusedKey}
            onBlurSlot={(key) => setFocusedKey((cur) => (cur === key ? null : cur))}
          />
        );
      })}

      {nodes.map((node, i) => (
        <NodeShape
          key={node.id}
          node={node}
          render={renders.get(node.id)}
          selected={selectedId === node.id}
          containsSelection={selectedInterface?.parentId === node.id}
          focused={focusedKey === `node:${node.id}`}
          tabIndex={i === focusIndex ? 0 : -1}
          nodeRef={(el) => (nodeRefs.current[i] = el)}
          onSelect={() => onSelect(node.id)}
          onKeyDown={handleNodeKeyDown(i)}
          onFocus={() => {
            setFocusIndex(i);
            setFocusedKey(`node:${node.id}`);
          }}
          onBlur={() => setFocusedKey((cur) => (cur === `node:${node.id}` ? null : cur))}
          transitioning={transitioning?.has(node.id) ?? false}
          sustainedMs={sustained?.get(node.id)}
          burst={bursts?.has(node.id) ?? false}
          animate={animate}
        />
      ))}
    </svg>
  );
}
